import { useState } from "react";
import { calculateOperationalKpis } from "../../../domain/simulation";
import type { FlightLedgerPreviewOverrides, FlightLedgerResult } from "../../../domain/simulation";
import type { AirportConfigSet, FlightReleaseDraft, GlobalEvent, RedispatchAction, SaveGame, ScheduleSource, WorldData } from "../../../domain/types";
import { type CustomFlightDraft, type RotationDraft } from "../../gameSession";
import { AirportDataView } from "./PlayerAirportData";
import { FleetView, LogsView } from "./PlayerFleetLogs";
import { OverviewView } from "./PlayerOverview";
import { PlanningView } from "./PlayerPlanning";
import { CustomFlightView } from "./PlayerRecovery";
import { TimelineView } from "./PlayerTimeline";
import { PLAYER_VIEWS, normalizePlayerView, type PlayerView } from "./playerTypes";


export function PlayerConsole(props: {
  save: SaveGame;
  world: WorldData;
  airportConfigs: AirportConfigSet;
  schedule: ScheduleSource;
  dailyProfit: number;
  openCustomFlightCount: number;
  activeEvents: GlobalEvent[];
  customFlightDraft: CustomFlightDraft;
  setCustomFlightDraft: (value: CustomFlightDraft) => void;
  onCreateGame: () => void;
  onStartOpsDay: () => void;
  onTickOps: (minutes: number) => void;
  onRedispatchFlight: (action: RedispatchAction) => Promise<void>;
  onFetchFlightReleaseDraft: (flightInstanceId: string) => Promise<FlightReleaseDraft>;
  onFetchFlightLedgerPreview: (flightInstanceId: string, overrides?: FlightLedgerPreviewOverrides) => Promise<FlightLedgerResult | undefined>;
  onCreateCustomFlight: () => void;
  onSaveRotation: (input: RotationDraft) => void;
  onDeleteRotation: (rotationId: string) => void;
}) {
  const [view, setView] = useState<PlayerView>(() => normalizePlayerView(new URLSearchParams(window.location.search).get("view")));
  const [selectedAirportCode, setSelectedAirportCode] = useState(() => new URLSearchParams(window.location.search).get("airport") ?? "");
  const [rotationDraft, setRotationDraft] = useState<RotationDraft>({
    name: "新航班串",
    occurrenceIds: [],
    scheduledFlightIds: []
  });
  const selectView = (nextView: PlayerView) => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", nextView);
    if (nextView !== "airports") {
      url.searchParams.delete("airportRecord");
      url.searchParams.delete("airport");
    }
    window.history.replaceState(null, "", url);
    setView(nextView);
  };
  const lastDayOperations = props.save.operationsLog.filter((operation) => operation.day === props.save.day - 1);
  const kpis = calculateOperationalKpis(lastDayOperations.flatMap((operation) => operation.legs));

  return (
    <section className="player-workspace">
      <nav className="player-subnav" aria-label="玩家二级界面">
        {PLAYER_VIEWS.map((item) => (
          <button key={item.id} className={playerViewButtonClassName(view, item.id)} onClick={() => selectView(item.id)}>
            {item.label}
          </button>
        ))}
      </nav>

      {view === "overview" && (
        <OverviewView
          save={props.save}
          world={props.world}
          schedule={props.schedule}
          kpis={kpis}
          dailyProfit={props.dailyProfit}
          openCustomFlightCount={props.openCustomFlightCount}
          activeEvents={props.activeEvents}
          onStartOpsDay={props.onStartOpsDay}
          onTickOps={props.onTickOps}
          onCreateGame={props.onCreateGame}
        />
      )}
      {view === "planning" && (
        <PlanningView
          save={props.save}
          schedule={props.schedule}
          draft={rotationDraft}
          setDraft={setRotationDraft}
          onSaveRotation={props.onSaveRotation}
          onDeleteRotation={props.onDeleteRotation}
        />
      )}
      {view === "timeline" && (
        <TimelineView
          save={props.save}
          world={props.world}
          schedule={props.schedule}
          activeEvents={props.activeEvents}
          onRedispatchFlight={props.onRedispatchFlight}
          onFetchFlightReleaseDraft={props.onFetchFlightReleaseDraft}
          onFetchFlightLedgerPreview={props.onFetchFlightLedgerPreview}
        />
      )}
      {view === "airports" && (
        <AirportDataView
          save={props.save}
          world={props.world}
          airportConfigs={props.airportConfigs}
          activeEvents={props.activeEvents}
          selectedAirportCode={selectedAirportCode}
          onSelectAirport={setSelectedAirportCode}
        />
      )}
      {view === "recovery" && (
        <CustomFlightView
          save={props.save}
          world={props.world}
          schedule={props.schedule}
          customFlightDraft={props.customFlightDraft}
          setCustomFlightDraft={props.setCustomFlightDraft}
          onCreateCustomFlight={props.onCreateCustomFlight}
        />
      )}
      {view === "fleet" && <FleetView save={props.save} />}
      {view === "logs" && <LogsView save={props.save} world={props.world} activeEvents={props.activeEvents} cancellations={props.schedule.cancellations} />}
    </section>
  );
}

function playerViewButtonClassName(activeView: PlayerView, itemView: PlayerView): string {
  if (activeView === itemView) {
    return "active";
  }
  return "";
}
